import { liveForceExitTime } from './forceExit'

type RatioPair = Record<string, unknown> & { risk?: unknown; reward?: unknown }
type TradeRatio = RatioPair & { conditions?: RatioPair[] }

export type AnalyzeTrades = Record<string, unknown>

/**
 * The `analyzeTrades` fields the live executor reads (executeTrade.ts), copied as-is.
 *
 * Everything else a backtest carries here is either simulation-only (`spread`,
 * `dynamicTakeProfit`) or superseded by the broker account at execution time
 * (`equity`, `contractSize`, `commissions`) — an alert that kept them would
 * imply settings it does not actually honour.
 *
 * `forceExit` is no longer simulation-only and is carried separately below: the
 * exit engine closes a live position on a time rule, but only on the `time`
 * shape, so it cannot be copied wholesale.
 */
const LIVE_ANALYZE_TRADE_FIELDS = [
  'riskPerTrade',
  'tradeLimit',
  'dailyTradeLimit',
  'allowHedging',
  'maxRisk',
] as const

export type GenerateTrades = Record<string, unknown> & {
  exitStrategy?: string
  incrementalStart?: unknown
  incrementalStop?: unknown
  incrementalStep?: unknown
  tradeRatios?: TradeRatio[]
}

function scaledPair(pair: RatioPair, start: number): RatioPair | null {
  const risk = Number(pair?.risk)
  const reward = Number(pair?.reward)
  if (!Number.isFinite(risk) || !Number.isFinite(reward)) {
    return null
  }
  const stopLoss = start * risk
  return { ...pair, risk: stopLoss, reward: stopLoss * reward }
}

/**
 * Converts an `incremental` exit strategy to the equivalent `fixed` one.
 *
 * Incremental produces one trade per stop-loss value between its start and
 * stop, which the live auto-trade executor does not support, so an alert takes
 * the starting stop-loss: SL = incrementalStart * risk, TP = SL * reward.
 *
 * Every conditional ratio is scaled the same way, because a matched condition
 * replaces the base ratio outright and `fixed` reads risk and reward as the
 * literal stop-loss and take-profit.
 *
 * Anything else is returned untouched, as is an incremental strategy whose
 * start value or ratio cannot be read as a number.
 */
function convertIncrementalToFixed<T extends GenerateTrades>(generateTrades: T): T {
  if (generateTrades.exitStrategy !== 'incremental') {
    return generateTrades
  }

  const start = Number(generateTrades.incrementalStart)
  if (!Number.isFinite(start) || start <= 0) {
    return generateTrades
  }

  const ratios = Array.isArray(generateTrades.tradeRatios) ? generateTrades.tradeRatios : []
  const converted = ratios.map((ratio) => {
    const scaled = scaledPair(ratio, start) ?? ratio
    if (!Array.isArray(ratio?.conditions)) {
      return scaled
    }
    return {
      ...scaled,
      conditions: ratio.conditions.map((condition) => scaledPair(condition, start) ?? condition),
    }
  })

  const {
    incrementalStart: _start,
    incrementalStop: _stop,
    incrementalStep: _step,
    ...rest
  } = generateTrades

  return { ...rest, exitStrategy: 'fixed', tradeRatios: converted } as unknown as T
}

/**
 * Makes a backtest's trade settings valid for an alert: converts an
 * incremental exit strategy, then keeps only the first risk/reward ratio.
 *
 * A backtest may carry up to fifteen ratios, an alert exactly one, and the
 * executor saves only the first trade it generates — so the remaining ratios
 * would sit in the alert unused and over its own limit.
 */
export default function normalizeTradeSettingsForAlert<T extends GenerateTrades>(
  generateTrades: T
): T {
  if (!generateTrades) {
    return generateTrades
  }

  const converted = convertIncrementalToFixed(generateTrades)
  const ratios = converted.tradeRatios
  if (!Array.isArray(ratios) || ratios.length <= 1) {
    return converted
  }

  return { ...converted, tradeRatios: ratios.slice(0, 1) }
}

/**
 * Narrows a backtest's account settings to the ones an alert actually uses.
 *
 * Deliberately an allow-list rather than a delete-list: a backtest may gain a
 * simulation-only setting at any time, and the failure mode of forgetting to
 * exclude it — an alert claiming a spread or starting equity the broker will
 * never apply — is silent. Unknown fields are dropped, not carried.
 */
export function normalizeAccountSettingsForAlert<T extends AnalyzeTrades>(
  analyzeTrades: T
): Partial<T> {
  if (!analyzeTrades) {
    return analyzeTrades
  }

  const normalized: Partial<T> = {}
  for (const field of LIVE_ANALYZE_TRADE_FIELDS) {
    if (analyzeTrades[field] !== undefined) {
      normalized[field as keyof T] = analyzeTrades[field] as T[keyof T]
    }
  }

  // Only the `time` shape, and only a usable one. A backtest's `forceExit` may also be
  // an empty object (the form's default) or a candle count, and the live engine honours
  // neither — carrying one would promise the trader a close nothing performs.
  const forceExit = liveForceExitTime(analyzeTrades)
  if (forceExit) {
    normalized['forceExit' as keyof T] = { time: forceExit } as T[keyof T]
  }

  return normalized
}
